import React, { useRef, useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { mediaUrl } from "@/lib/media";

const MAX_MB = 10;

export default function ImageInput({ value, onChange, label = "Image" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`Image too large. Max ${MAX_MB}MB.`);
      e.target.value = "";
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <div className="text-sm font-500 text-slate-700">{label}</div>
      <div className="flex items-center gap-4">
        <div className="grid h-20 w-20 place-items-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
          {value ? <img src={mediaUrl(value)} alt="preview" className="h-full w-full object-cover" /> : <Upload size={18} className="text-slate-400" />}
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-600 text-slate-700 hover:border-brand hover:text-brand disabled:opacity-70" data-testid="image-upload-btn">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload
          </button>
          {value && (
            <button type="button" onClick={() => onChange("")} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-600 text-red-500 hover:border-red-300" data-testid="image-remove-btn"><X size={15} /></button>
          )}
        </div>
      </div>
      <input ref={ref} type="file" accept="image/*" onChange={handle} className="hidden" data-testid="image-file-input" />
    </div>
  );
}
